import { Typography, Box } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import React from "react";
import { ChevronUp, ChevronDown } from "lucide-react";
import { colorSystem } from "../../../theme";
import dayjs from "dayjs";

const useStyles = makeStyles((theme) => ({
  highlightItem: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
  },

  valuesBox: {
    display: "flex",
    flexDirection: "row",
    gap: theme.spacing(1.25),
    alignItems: "center",
  },
  numbers: {
    fontSize: 14,
    fontWeight: 600,
  },
  itemLabel: {
    fontSize: 14,
    fontWeight: 400,
  },
  itemIcon: {
    color: theme.palette.text.secondary,
    fontSize: 16,
  },
  success: {
    color: colorSystem.green2,
  },
  error: {
    color: theme.palette.error.light,
  },
  neutral: {
    color: theme.palette.text.secondary,
  },
  total: {
    fontSize: 10,
    fontWeight: 800,
    lineHeight: "12px",
    color: theme.palette.common.black,
    backgroundColor: colorSystem.gray9,
    borderRadius: 4,
    width: "fit-content",
    padding: theme.spacing(0.2, 0.8),
    textTransform: "uppercase",
  },
}));

export enum HighlightType {
  VOLUME = "VOLUME",
  REPS = "REPS",
  METRIC = "METRIC",
  TIME = "TIME",
}

export interface Highlights {
  volume?: HighlightItemInfo;
  reps?: HighlightItemInfo;
  metric?: HighlightItemInfo;
}

export type Unit = "kg" | "lbs" | "reps" | "min" | "h";

export interface HighlightValue {
  current: number;
  previous?: number;
  unit?: Unit;
}

export interface HighlightItemInfo {
  type: HighlightType;
  icon: React.ReactNode;
  value: HighlightValue;
  showDiffs?: boolean;
  isCurrentMonth?: boolean;
}

interface IHighlightItemProps {
  highlight: HighlightItemInfo;
}

const formatValue = (type: HighlightType, value: number) => {
  if (type === HighlightType.TIME) {
    const d = dayjs.duration(value, "minutes");
    const hours = d.hours();
    const minutes = d.minutes();
    if (hours > 0 && minutes > 0) return `${hours}h ${minutes}min`;
    if (hours > 0) return `${hours}h`;
    return `${minutes}min`;
  }

  return value.toLocaleString("en-US");
};

const HighlightItem = ({ highlight }: IHighlightItemProps) => {
  const s = useStyles();
  const {
    type,
    icon,
    value,
    showDiffs = true,
    isCurrentMonth = false,
  } = highlight;

  const { current = 0, previous = 0, unit } = value;

  const diff = showDiffs && previous !== undefined ? current - previous : null;

  const isNegative = diff !== null && diff < 0;
  const isPositive = diff !== null && diff > 0;
  const isZero = diff === 0;

  const getTrendIcon = () => {
    if (isPositive || isZero) return <ChevronUp size={16} />;
    if (isNegative) return <ChevronDown size={16} />;
  };

  const trendIcon = getTrendIcon();

  // if current month and negative value, neutral trend color
  // if current month and positive value, success trend color
  const trendClass =
    diff === null || isZero || (isNegative && isCurrentMonth)
      ? s.neutral
      : isPositive
        ? s.success
        : s.error;

  // if current month and negative value, show "to go" message
  const displayToGoMessage = isCurrentMonth && isNegative;

  return (
    <Box key={type} className={s.highlightItem}>
      <Typography variant="h6" className={s.total} children={"Total"} />
      <span className={s.itemIcon}>{icon}</span>
      <Box className={s.valuesBox}>
        <Typography className={s.numbers}>
          {formatValue(type, current)}
          {type !== HighlightType.TIME && unit ? ` ${unit}` : ""}
        </Typography>

        {diff !== null && !isNaN(diff) && (
          <Box
            display="flex"
            alignItems="center"
            gap={0.5}
            className={trendClass}
          >
            {trendIcon}
            <Typography fontSize={14} className={s.numbers}>
              <>
                {formatValue(type, Math.abs(diff))}
                {type !== HighlightType.TIME && unit ? ` ${unit}` : ""}
                {displayToGoMessage && " to go"}
              </>
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default HighlightItem;
